import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { LotData } from '../../../panel/panel.model';
import { AppParamItems, AppParamsService } from '../../app-params.service';
import { CurrencyType } from '../../currency/currency-types';
import { ExchangeMoneyFormatter, ExchangeRates } from '../../exchange-formatter/exchange-formatter';
import { AuctionLotBiddingStatus } from './auction-lot-bidding-status';

@Component({
  selector: 'app-auction-event-info',
  templateUrl: './auction-event-info.component.html',
  styleUrls: ['./auction-event-info.component.scss'],
})
export class AuctionEventInfoComponent implements OnChanges, OnDestroy {
  @Input() auctionId: string | null = null;
  @Input() lotData: LotData | null = null;
  @Input() biddingStatus: AuctionLotBiddingStatus | null = null;
  @Input() isPaused = false;
  @Input() cutting = false;
  @Input() editing = false;
  @Input() extendedDetails = false;
  @Input() large = false;
  @Input() showEditButton = false;

  @Output() editLot = new EventEmitter<void>();

  @HostBinding('class.auction-event-info--large')
  get isLarge(): boolean {
    return this.large;
  }

  public exchangeRates: ExchangeRates[] = [];

  private paramSubscription = new Subscription();

  constructor(
    private readonly exchangeMoneyFormatter: ExchangeMoneyFormatter,
    private readonly params: AppParamsService,
  ) {}

  get displayLotCode(): string {
    return this.lotData?.code ?? '-';
  }

  get showBiddingIndicator(): boolean {
    return this.biddingStatus !== null;
  }

  get isReceivingBids(): boolean {
    return this.biddingStatus === AuctionLotBiddingStatus.ReceivingBids;
  }

  get hasLotContent(): boolean {
    return !!this.lotData;
  }

  onEditClick(): void {
    this.editLot.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['auctionId']) {
      return;
    }
    this.teardownCurrencyWatch();
    if (!this.auctionId) {
      this.exchangeRates = [];
      return;
    }
    this.setupCurrencyParams(this.auctionId);
  }

  ngOnDestroy(): void {
    this.teardownCurrencyWatch();
  }

  private teardownCurrencyWatch(): void {
    this.paramSubscription.unsubscribe();
    this.paramSubscription = new Subscription();
  }

  private setupCurrencyParams(auctionId: string): void {
    void this.params.dispose(AppParamItems.AppParamGeneral.primaryCurrencyType).then(() => {
      const sub = this.params
        .watchFor(auctionId, AppParamItems.AppParamGeneral.primaryCurrencyType)
        .subscribe((primaryCurrencyType) => {
          const format = this.exchangeMoneyFormatter.getFormat(primaryCurrencyType);
          if (format.requiresMultiMoney) {
            void this.params.dispose(AppParamItems.AppParamGeneral.exchangeRate).then(() => {
              const subInner = this.params
                .watchFor(auctionId, AppParamItems.AppParamGeneral.exchangeRate)
                .subscribe((exchangeRate) => {
                  this.exchangeRates = [
                    { currencyCode: primaryCurrencyType, exchangeRate: 1 },
                    { currencyCode: CurrencyType.USD, exchangeRate: parseFloat(String(exchangeRate)) || 0 },
                  ];
                });
              this.paramSubscription.add(subInner);
            });
          } else {
            this.exchangeRates = [{ currencyCode: CurrencyType.USD, exchangeRate: 1 }];
          }
        });
      this.paramSubscription.add(sub);
    });
  }
}
